import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import { ChipEditor, parseList } from "../components/ChipEditor.js";
import { fileToWide } from "../lib/image.js";
import { COLLECTION_CONFIGS } from "./config.js";

const str = (v: unknown) => (typeof v === "string" ? v : "");
const ACCEPT = "image/jpeg,image/png,image/webp";
const CATEGORIES: string[] = COLLECTION_CONFIGS.storyBible?.fields.find((f) => f.key === "category")?.options ?? [];

interface SceneRef { id: string; title: string; chapterId: string; chapterTitle: string }
interface Source { sceneId: string; quote?: string }

function parseSources(raw: unknown): Source[] {
  const s = str(raw);
  if (!s.trim()) return [];
  try {
    const arr = JSON.parse(s);
    return Array.isArray(arr) ? arr.filter((x) => x && typeof x.sceneId === "string") : [];
  } catch { return []; }
}

type Status = "canonical" | "accepted" | "needs_review";
function statusOf(item: CollectionItem): Status {
  if (item.canonical) return "canonical";
  if (str(item.reviewStatus) === "needs_review") return "needs_review";
  return "accepted";
}
const STATUS_LABEL: Record<Status, string> = { canonical: "Canonical", accepted: "Accepted", needs_review: "Needs review" };
const CONF_PCT: Record<string, number> = { high: 100, medium: 60, low: 30 };

export function StoryBiblePanel({ projectId, sceneRefs, onOpenScene, focusId }: {
  projectId: string;
  sceneRefs: SceneRef[];
  onOpenScene: (sceneId: string) => void;
  focusId?: string | null;
}) {
  const toast = useToast();
  const [items, setItems] = useState<CollectionItem[] | null>(null);
  const [characters, setCharacters] = useState<CollectionItem[]>([]);
  const [locations, setLocations] = useState<CollectionItem[]>([]);
  const [dupeGroups, setDupeGroups] = useState<{ items: { id: string; name: string }[] }[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [pendingDelete, setPendingDelete] = useState<CollectionItem | null>(null);

  const load = useCallback(async () => {
    const [sb, chars, locs] = await Promise.all([
      api.listCollection(projectId, "story-bible"),
      api.listCollection(projectId, "characters"),
      api.listCollection(projectId, "locations")
    ]);
    setItems(sb.items);
    setCharacters(chars.items);
    setLocations(locs.items);
    setSelectedId((cur) => cur ?? focusId ?? sb.items[0]?.id ?? null);
    api.duplicateCandidates(projectId, "story-bible").then((r) => setDupeGroups(r.groups)).catch(() => setDupeGroups([]));
  }, [projectId, focusId]);

  useEffect(() => {
    setItems(null); setSelectedId(null); setQuery(""); setCat("all");
    load().catch((err) => toast.error(err instanceof Error ? err.message : "Failed to load"));
  }, [load, toast]);
  useEffect(() => { if (focusId) setSelectedId(focusId); }, [focusId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items ?? [])
      .filter((it) => cat === "all" || str(it.category) === cat)
      .filter((it) => !q || str(it.title).toLowerCase().includes(q) || str(it.value).toLowerCase().includes(q));
  }, [items, query, cat]);

  const selected = items?.find((i) => i.id === selectedId) ?? null;

  const create = async () => {
    try {
      const { item } = await api.createCollectionItem(projectId, "story-bible", { title: "New Entry", category: "Important facts" });
      setItems((prev) => (prev ? [item, ...prev] : [item]));
      setSelectedId(item.id);
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to create"); }
  };
  const onChanged = (updated: CollectionItem) =>
    setItems((prev) => (prev ? prev.map((i) => (i.id === updated.id ? updated : i)) : prev));

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id; setPendingDelete(null);
    try {
      await api.deleteCollectionItem("story-bible", id);
      setItems((prev) => {
        const next = prev ? prev.filter((i) => i.id !== id) : prev;
        if (selectedId === id) setSelectedId(next?.[0]?.id ?? null);
        return next;
      });
      toast.success("Entry deleted.");
    } catch (err) { toast.error(err instanceof Error ? err.message : "Failed to delete"); }
  };

  const merge = async (keepId: string, dupId: string) => {
    try {
      await api.mergeCollection(projectId, "story-bible", keepId, [dupId]);
      toast.success("Entries merged.");
      setSelectedId(keepId);
      await load();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Merge failed"); }
  };

  return (
    <div className="loc">
      <div className="loc-list">
        <div className="loc-list-head">
          <div className="loc-list-title"><Icon name="book" size={18} style={{ color: "var(--accent)" }} /> <span>Story Bible</span></div>
          <button className="btn btn-icon btn-primary btn-sm" title="New entry" onClick={create}><Icon name="plus" size={15} /></button>
        </div>
        <div className="loc-search">
          <Icon name="search" size={14} />
          <input placeholder="Search entries…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="sb-catfilter">
          <select className="select" value={cat} onChange={(e) => setCat(e.target.value)}>
            <option value="all">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="loc-items">
          {items === null ? (
            <div className="collection-loading"><Icon name="clock" size={16} className="spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="collection-empty-list">
              <p className="muted">{query || cat !== "all" ? "No matches." : "No entries yet."}</p>
              {!query && cat === "all" && <button className="btn btn-sm" onClick={create}><Icon name="plus" size={14} /> New entry</button>}
            </div>
          ) : (
            filtered.map((item) => (
              <div key={item.id} className={`loc-item ${item.id === selectedId ? "active" : ""}`}>
                <button className="loc-item-main" onClick={() => setSelectedId(item.id)}>
                  <span className="sb-list-ico"><Icon name="file" size={15} /></span>
                  <span className="grow" style={{ minWidth: 0 }}>
                    <span className="loc-item-name truncate">
                      {str(item.title) || "(untitled)"}
                      {item.origin === "ai" && <span className="loc-ai">AI</span>}
                    </span>
                    <span className="loc-item-sub truncate">{str(item.category) || "Uncategorized"}</span>
                  </span>
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="loc-detail-wrap">
        {selected ? (
          <EntryDetail
            key={selected.id}
            item={selected}
            sceneRefs={sceneRefs}
            characters={characters}
            locations={locations}
            allEntries={items ?? []}
            dupeGroups={dupeGroups}
            onChanged={onChanged}
            onDelete={() => setPendingDelete(selected)}
            onOpenScene={onOpenScene}
            onSelect={setSelectedId}
            onMerge={merge}
          />
        ) : (
          <div className="collection-detail-empty">
            <Icon name="book" size={40} style={{ color: "var(--text-faint)" }} />
            <h2>Story Bible</h2>
            <p className="muted">Select an entry, or create one.</p>
            <button className="btn btn-primary" onClick={create}><Icon name="plus" size={15} /> New entry</button>
          </div>
        )}
      </div>

      {pendingDelete && (
        <ConfirmDialog
          title="Delete entry"
          message={`Delete “${str(pendingDelete.title) || "this entry"}”? This cannot be undone.`}
          confirmLabel="Delete" danger
          onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}

function EntryDetail({ item, sceneRefs, characters, locations, allEntries, dupeGroups, onChanged, onDelete, onOpenScene, onSelect, onMerge }: {
  item: CollectionItem;
  sceneRefs: SceneRef[];
  characters: CollectionItem[];
  locations: CollectionItem[];
  allEntries: CollectionItem[];
  dupeGroups: { items: { id: string; name: string }[] }[];
  onChanged: (item: CollectionItem) => void;
  onDelete: () => void;
  onOpenScene: (id: string) => void;
  onSelect: (id: string) => void;
  onMerge: (keepId: string, dupId: string) => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [addingSource, setAddingSource] = useState(false);
  const heroInput = useRef<HTMLInputElement>(null);

  const [vals, setVals] = useState<Record<string, string>>(() => ({
    title: str(item.title), value: str(item.value), content: str(item.content)
  }));
  const set = (k: string, v: string) => setVals((s) => ({ ...s, [k]: v }));

  const sceneMap = useMemo(() => new Map(sceneRefs.map((s) => [s.id, s])), [sceneRefs]);
  const charMap = useMemo(() => new Map(characters.map((c) => [c.id, c])), [characters]);
  const locMap = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);

  // Legacy single source merges into the sources list for display.
  const sources: Source[] = useMemo(() => {
    const parsed = parseSources(item.sources);
    if (parsed.length === 0 && str(item.sourceSceneId)) {
      return [{ sceneId: str(item.sourceSceneId), quote: str(item.evidence) || undefined }];
    }
    return parsed;
  }, [item.sources, item.sourceSceneId, item.evidence]);

  const linkedChars = parseList(item.linkedCharacterIds).map((id) => charMap.get(id)).filter(Boolean) as CollectionItem[];
  const linkedLocs = parseList(item.linkedLocationIds).map((id) => locMap.get(id)).filter(Boolean) as CollectionItem[];

  const status = statusOf(item);
  const confidence = str(item.confidence).toLowerCase();
  const tags = parseList(item.tags);

  // First/last referenced chapters, in manuscript reading order.
  const orderedSources = useMemo(() => {
    const idx = new Map(sceneRefs.map((s, i) => [s.id, i]));
    return [...sources].filter((s) => idx.has(s.sceneId)).sort((a, b) => (idx.get(a.sceneId)! - idx.get(b.sceneId)!));
  }, [sources, sceneRefs]);
  const firstChapter = orderedSources[0] ? sceneMap.get(orderedSources[0].sceneId)?.chapterTitle : null;
  const lastChapter = orderedSources.length ? sceneMap.get(orderedSources[orderedSources.length - 1]!.sceneId)?.chapterTitle : null;

  const dupes = useMemo(() => {
    const group = dupeGroups.find((g) => g.items.some((i) => i.id === item.id));
    if (!group) return [];
    return group.items.filter((i) => i.id !== item.id);
  }, [dupeGroups, item.id]);

  const persist = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try { const { item: u } = await api.updateCollectionItem("story-bible", item.id, patch); onChanged(u); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed to save"); }
    finally { setSaving(false); }
  };
  const commit = (key: string, value: string) => { if (str(item[key]) === value) return; void persist({ [key]: value || null }); };
  const saveSources = (next: Source[]) => void persist({ sources: next.length ? JSON.stringify(next) : null });

  const pickHero = async (file: File | null) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG, or WebP image."); return; }
    try { await persist({ imageData: await fileToWide(file) }); toast.success("Image updated."); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't set image"); }
  };

  const setStatus = (next: Status) =>
    void persist(next === "canonical" ? { canonical: true, reviewStatus: "accepted" }
      : { canonical: false, reviewStatus: next === "needs_review" ? "needs_review" : "accepted" });

  // Unlinked candidates for the "link element" picker.
  const linkableChars = characters.filter((c) => !parseList(item.linkedCharacterIds).includes(c.id));
  const linkableLocs = locations.filter((l) => !parseList(item.linkedLocationIds).includes(l.id));
  const addLink = (kind: "char" | "loc", id: string) => {
    if (kind === "char") void persist({ linkedCharacterIds: [...parseList(item.linkedCharacterIds), id].join(",") });
    else void persist({ linkedLocationIds: [...parseList(item.linkedLocationIds), id].join(",") });
    setLinking(false);
  };
  const removeLink = (kind: "char" | "loc", id: string) => {
    if (kind === "char") void persist({ linkedCharacterIds: parseList(item.linkedCharacterIds).filter((x) => x !== id).join(",") || null });
    else void persist({ linkedLocationIds: parseList(item.linkedLocationIds).filter((x) => x !== id).join(",") || null });
  };

  const usedSceneIds = new Set(sources.map((s) => s.sceneId));
  const addableScenes = sceneRefs.filter((s) => !usedSceneIds.has(s.id));

  return (
    <div className="sb-detail">
      <div className="sb-head">
        <div className="loc-crumb" style={{ color: "var(--text-faint)" }}><span style={{ letterSpacing: "0.08em", fontWeight: 700, fontSize: 10.5 }}>STORY BIBLE</span></div>
        <div className="sb-head-row">
          <input className="detail-title sb-title" value={vals.title}
            onChange={(e) => set("title", e.target.value)} onBlur={(e) => commit("title", e.target.value)} placeholder="Entry title" />
          <div className="row gap-2">
            <span className="detail-saved faint">{saving ? "Saving…" : "Saved"}</span>
            <button className="btn btn-icon btn-ghost btn-danger" title="Delete entry" onClick={onDelete}><Icon name="trash" size={15} /></button>
          </div>
        </div>
        <div className="sb-chips">
          {str(item.category) && <span className="chip chip-accent">{str(item.category)}</span>}
          <span className={`chip sb-status sb-status-${status}`}>
            <Icon name={status === "needs_review" ? "alert" : "check"} size={12} /> {STATUS_LABEL[status]}
          </span>
        </div>
      </div>

      <div className="sb-body">
        <div className="sb-main">
          {/* Hero image */}
          <div className={`sb-banner ${item.imageData ? "has-img" : ""}`} style={item.imageData ? { backgroundImage: `url(${str(item.imageData)})` } : undefined}>
            <button className="btn btn-sm btn-ghost ov-glass sb-banner-edit" onClick={() => heroInput.current?.click()}>
              <Icon name="image" size={13} /> {item.imageData ? "Edit image" : "Add image"}
            </button>
            {!item.imageData && <div className="sb-banner-empty"><Icon name="image" size={22} /><span>Add an image for this entry</span></div>}
            <input ref={heroInput} type="file" accept={ACCEPT} style={{ display: "none" }}
              onChange={(e) => { void pickHero(e.target.files?.[0] ?? null); e.target.value = ""; }} />
          </div>

          {/* Key information */}
          <section className="card pad">
            <div className="sb-card-head"><Icon name="info" size={15} style={{ color: "var(--accent)" }} /><h3>Key information</h3></div>
            <div className="sb-keyinfo">
              <div className="sb-value">
                <label className="label">Value</label>
                <input className="sb-value-input" value={vals.value} placeholder="—"
                  onChange={(e) => set("value", e.target.value)} onBlur={(e) => commit("value", e.target.value)} />
              </div>
              <div className="sb-desc">
                <label className="label">Description</label>
                <textarea className="textarea" rows={3} value={vals.content} placeholder="What does this entry record?"
                  onChange={(e) => set("content", e.target.value)} onBlur={(e) => commit("content", e.target.value)} />
              </div>
            </div>
          </section>

          {/* Sources in manuscript */}
          <section className="card pad">
            <div className="sb-card-head"><Icon name="file" size={15} style={{ color: "var(--accent)" }} /><h3>Sources in manuscript</h3></div>
            <div className="sb-sources">
              {sources.length === 0 && <p className="muted" style={{ margin: 0, fontSize: 13 }}>No sources linked yet.</p>}
              {sources.map((srcEntry, i) => {
                const sc = sceneMap.get(srcEntry.sceneId);
                return (
                  <div key={i} className="sb-source">
                    <div className="sb-source-top">
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="sb-source-title"><Icon name="file" size={13} style={{ color: "var(--accent)" }} /> {sc ? `${sc.chapterTitle} – ${sc.title}` : "Scene not found"}</div>
                        <input key={`${srcEntry.sceneId}-${i}`} className="sb-source-quote" defaultValue={srcEntry.quote ?? ""} placeholder="Add a short quote…"
                          onBlur={(e) => {
                            const q = e.target.value.trim();
                            if ((srcEntry.quote ?? "") === q) return;
                            saveSources(sources.map((x, j) => (j === i ? { ...x, quote: q || undefined } : x)));
                          }} />
                      </div>
                      <div className="row gap-2" style={{ flex: "none" }}>
                        {sc && <button className="btn btn-sm btn-ghost" onClick={() => onOpenScene(srcEntry.sceneId)}>View in manuscript <Icon name="chevronRight" size={13} /></button>}
                        <button className="btn btn-icon btn-ghost btn-sm" title="Remove source" onClick={() => saveSources(sources.filter((_, j) => j !== i))}><Icon name="x" size={13} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {addingSource ? (
                <select className="select" autoFocus defaultValue=""
                  onChange={(e) => { if (e.target.value) { saveSources([...sources, { sceneId: e.target.value }]); setAddingSource(false); } }}
                  onBlur={() => setAddingSource(false)}>
                  <option value="">Choose a scene…</option>
                  {addableScenes.map((s) => <option key={s.id} value={s.id}>{s.chapterTitle} – {s.title}</option>)}
                </select>
              ) : (
                addableScenes.length > 0 && <button className="btn btn-sm btn-ghost sb-add" onClick={() => setAddingSource(true)}><Icon name="plus" size={13} /> Add another source</button>
              )}
            </div>
          </section>

          {/* Connected story elements */}
          <section className="card pad">
            <div className="sb-card-head"><Icon name="thread" size={15} style={{ color: "var(--accent)" }} /><h3>Connected story elements</h3>
              <span className="grow" />
              <button className="btn btn-sm btn-ghost" onClick={() => setLinking((v) => !v)}><Icon name="plus" size={13} /> Link</button>
            </div>
            {linking && (
              <select className="select" style={{ marginBottom: 10 }} autoFocus defaultValue=""
                onChange={(e) => { const v = e.target.value; if (!v) return; const [kind, id] = v.split(":"); addLink(kind as "char" | "loc", id!); }}
                onBlur={() => setLinking(false)}>
                <option value="">Link a character or location…</option>
                {linkableChars.length > 0 && <optgroup label="Characters">{linkableChars.map((c) => <option key={c.id} value={`char:${c.id}`}>{str(c.name)}</option>)}</optgroup>}
                {linkableLocs.length > 0 && <optgroup label="Locations">{linkableLocs.map((l) => <option key={l.id} value={`loc:${l.id}`}>{str(l.name)}</option>)}</optgroup>}
              </select>
            )}
            {linkedChars.length === 0 && linkedLocs.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>No connected characters or locations yet.</p>
            ) : (
              <div className="sb-connected">
                {linkedChars.map((c) => (
                  <div key={c.id} className="sb-conn">
                    <CharacterAvatar name={str(c.name)} imageUrl={c.imageData as string | null | undefined} size="md" />
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="truncate strong" style={{ fontSize: 13 }}>{str(c.name)}</div>
                      <div className="truncate faint" style={{ fontSize: 11 }}>{str(c.occupation) || "Character"}</div>
                    </div>
                    <button className="sb-conn-del" title="Unlink" onClick={() => removeLink("char", c.id)}><Icon name="x" size={12} /></button>
                  </div>
                ))}
                {linkedLocs.map((l) => (
                  <div key={l.id} className="sb-conn">
                    <CharacterAvatar name={str(l.name)} imageUrl={l.imageData as string | null | undefined} size="md" />
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="truncate strong" style={{ fontSize: 13 }}>{str(l.name)}</div>
                      <div className="truncate faint" style={{ fontSize: 11 }}>{str(l.type) || "Location"}</div>
                    </div>
                    <button className="sb-conn-del" title="Unlink" onClick={() => removeLink("loc", l.id)}><Icon name="x" size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="loc-aside">
          <section className="card pad">
            <div className="sb-card-head"><Icon name="file" size={15} style={{ color: "var(--accent)" }} /><h3>Status &amp; metadata</h3></div>
            <div className="sb-meta">
              <div className="sb-meta-row">
                <label className="label">Category</label>
                <select className="select" value={str(item.category)} onChange={(e) => void persist({ category: e.target.value || null })}>
                  <option value="">—</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sb-meta-row">
                <label className="label">Status</label>
                <select className="select" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
                  <option value="canonical">Canonical</option>
                  <option value="accepted">Accepted</option>
                  <option value="needs_review">Needs review</option>
                </select>
              </div>
              <div className="sb-meta-row">
                <label className="label">Confidence</label>
                <div className="sb-conf">
                  <select className="select" value={confidence || ""} onChange={(e) => void persist({ confidence: e.target.value || null })}>
                    <option value="">—</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <div className="sb-conf-bar"><div style={{ width: `${CONF_PCT[confidence] ?? 0}%` }} /></div>
                </div>
              </div>
              <div className="sb-meta-row">
                <label className="label">First established</label>
                <div className="sb-meta-val"><Icon name="timeline" size={13} className="faint" /> {firstChapter || "—"}</div>
              </div>
              <div className="sb-meta-row">
                <label className="label">Last referenced</label>
                <div className="sb-meta-val"><Icon name="timeline" size={13} className="faint" /> {lastChapter || "—"}</div>
              </div>
              <div className="sb-meta-row">
                <label className="label">Tags</label>
                <ChipEditor chips={tags} placeholder="Add tag" accent onChange={(next) => void persist({ tags: next.join(", ") || null })} />
              </div>
            </div>
          </section>

          <section className="card pad">
            <div className="sb-card-head"><Icon name="sparkles" size={15} style={{ color: "#a78bfa" }} /><h3>AI insights</h3><span className="sb-beta">BETA</span></div>
            {dupes.length === 0 ? (
              <div className="sb-ok">
                <Icon name="check" size={16} />
                <div>
                  <div className="strong" style={{ fontSize: 13 }}>No conflicts found</div>
                  <div className="faint" style={{ fontSize: 11.5 }}>This entry is consistent across your Story Bible — no likely duplicates detected.</div>
                </div>
              </div>
            ) : (
              <div className="sb-dupes">
                <div className="row gap-2" style={{ marginBottom: 8 }}>
                  <Icon name="info" size={14} className="faint" />
                  <span className="label" style={{ margin: 0 }}>Potential duplicates</span>
                </div>
                {dupes.map((d) => {
                  const full = allEntries.find((e) => e.id === d.id);
                  return (
                    <div key={d.id} className="sb-dupe">
                      <button className="sb-dupe-main" onClick={() => onSelect(d.id)}>
                        <span className="sb-dupe-thumb">{full?.imageData ? <img src={str(full.imageData)} alt="" /> : <Icon name="file" size={14} />}</span>
                        <span className="grow" style={{ minWidth: 0 }}>
                          <span className="truncate strong" style={{ fontSize: 12.5, display: "block" }}>{d.name}</span>
                          <span className="truncate faint" style={{ fontSize: 11, display: "block" }}>{str(full?.category) || "Entry"} · possible duplicate</span>
                        </span>
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={() => onMerge(item.id, d.id)}><Icon name="layers" size={13} /> Merge</button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
